<template>
  <div class="h-dvh flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />
    <CentralHeader
      :title="'Agents'"
      :count="isLoading ? null : filteredAgents?.length"
      @search="filterAgents"
      :route-name="'CreateAgentPage'"
      :hasFilter="true"
      :showFilter="showFilter"
      :toggleFilter="toggleFilter"
      :filterApplied="hasActiveFilters"
    />

    <!-- Filter Overlay -->
    <FilterOverlay
      v-if="showFilter"
      :title="'Filter by Project(s)'"
      @cancel="toggleFilter"
      :filters="dynamicFilters"
      @filter-change="handleFilterChange"
      @apply-filters="handleApplyFilters"
      @clear="clearAllFilters"
    />
    <!-- Main Content -->
    <div class="flex-1 flex flex-col mb-14 overflow-y-auto">
      <div
        v-if="isLoading"
        class="text-center flex-1 flex justify-center items-center m-auto h-full"
      >
        Loading agents...
      </div>
      <div
        v-else-if="errorMessage"
        class="text-center flex-1 flex justify-center items-center m-auto h-full text-red-500"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="Array.isArray(filteredAgents) && filteredAgents?.length === 0"
        class="text-center flex-1 flex justify-center gap-3 items-center m-auto h-full text-total-white-500"
      >
        <div v-if="isSearchMode">No agents available.</div>
        <button
          v-else
          @click="goToCreateAgent"
          class="text-prime-golden flex items-center gap-3"
        >
          <i class="fa fa-plus text-2xl mr-2"></i>
          Click to add agents
        </button>
      </div>
      <div v-else>
        <div v-for="(agent, index) in filteredAgents" :key="agent.id">
          <AgentCard :agent="agent" :index="index" />
        </div>
      </div>
    </div>

    <!-- Pagination Controls (Fixed at the bottom) -->
    <div
      class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
    >
      <!-- Pagination in Template -->
      <div
        class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
      >
        <button
          @click="previousPage"
          :disabled="currentPage === 1 || isLoading"
          class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
        >
          <i class="fa fa-chevron-left"></i>
        </button>

        <span class="text-sm text-total-white mx-4">
          {{ currentPage }} / {{ totalPages }}
        </span>

        <button
          @click="nextPage"
          :disabled="currentPage >= totalPages || isLoading"
          class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
        >
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useAgentStore } from "@/stores/agent"; // Assuming you have an agent store
import TopNavbar from "@/components/TopNavbar.vue";
import CentralHeader from "@/components/CentralHeader.vue";
import AgentCard from "@/components/AgentCard.vue";
import { computed } from "vue";
import FilterOverlay from "@/components/FilterOverlay.vue";
import { useProjectStore } from "@/stores/project";
import { useRouter } from "vue-router";

const agentStore = useAgentStore();
const projectStore = useProjectStore();
const agents = ref([]);
const isLoading = ref(false);
const errorMessage = ref("");
const currentPage = ref(1);
const totalPages = ref(1);
const filteredAgents = ref([]);
const isSearchMode = ref(false);
const projects = ref([]);
const dynamicFilters = ref([]);
const totalAgents = ref(0);
const router = useRouter();

onMounted(async () => {
  await fetchAgents();
  await projectStore.fetchAllProjects(
    1,
    100
  ); /*TODO: replace with non-paginated version*/
  projects.value = projectStore.projects;
  dynamicFilters.value = [
    {
      key: "projects",
      label: "Projects",
      type: "multi-select",
      options: projects.value.map((project) => ({
        id: project.id,
        label: project.address_1,
      })),
      selected: [],
      dropdownOpen: false,
      searchQuery: "",
    },
  ];
});

const showFilter = ref(false);

// Active filters state
const activeFilters = ref({
  agentType: [],
  agentStatus: [],
});

// Handle Apply Filters
const handleApplyFilters = async (appliedFilters) => {
  const projectIds = appliedFilters[0].selected?.map((project) => project.id) || [];

  console.log("Project IDs:", projectIds);
  try {
    if (isSearchMode.value) {
      await agentStore.searchAgents(
        query,
        currentPage.value,
        agentStore.pageSize,
        projectIds
      );
    } else {
      await agentStore.fetchAgents(currentPage.value, agentStore.pageSize, projectIds);
    }
    filteredAgents.value = agentStore.agents;
  } catch (error) {
    errorMessage.value = "Failed to apply filters.";
    console.error("Error applying filters:", error);
  } finally {
    showFilter.value = false;
  }
};

// Toggle filter overlay
const toggleFilter = () => {
  showFilter.value = !showFilter.value;
};

// Handle filter changes
const handleFilterChange = (filterKey, selectedValues) => {
  activeFilters.value[filterKey] = selectedValues;
  console.log("Active Filters:", activeFilters.value);
};

// Check if there are active filters
const hasActiveFilters = computed(() => {
  return Object.values(activeFilters.value).some((filter) => filter.length > 0);
});

// Clear all filters
const clearAllFilters = async () => {
  activeFilters.value = {
    agentType: [],
    agentStatus: [],
    projects: [],
  };
  try {
    if (isSearchMode.value) {
      await agentStore.searchAgents(query, currentPage.value, agentStore.pageSize);
    } else {
      await agentStore.fetchAgents(currentPage.value, agentStore.pageSize);
    }
    filteredAgents.value = agentStore.agents;
  } catch (error) {
    errorMessage.value = "Failed to clear filters.";
    console.error("Error clearing filters:", error);
  }
};

// Fetch agents
const fetchAgents = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const filters = activeFilters.value.projects?.map((project) => project.id) || [];
    await agentStore.fetchAgents(currentPage.value, agentStore.pageSize, filters);
    agents.value = agentStore.agents;
    totalAgents.value = agentStore.totalAgents;
    totalPages.value = Math.ceil(totalAgents.value / agentStore.pageSize);
    filteredAgents.value = agents.value;
  } catch (error) {
    errorMessage.value = "Failed to load agents.";
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

// Filter agents based on search query using the search endpoint
const filterAgents = async (query) => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    isSearchMode.value = !!query;
    const filters = activeFilters.value.projects?.map((project) => project.id) || [];

    currentPage.value = 1; // Reset to the first page on search
    if (query) {
      await agentStore.searchAgents(
        query,
        currentPage.value,
        agentStore.pageSize,
        filters
      );
      totalAgents.value = agentStore.totalAgents;
      totalPages.value = Math.ceil(totalAgents.value / agentStore.pageSize);
    } else {
      await fetchAgents();
    }
    filteredAgents.value = agentStore.agents;
  } catch (error) {
    errorMessage.value = "Failed to search agents.";
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

// On Mounted
onMounted(async () => {
  await fetchAgents();
});

// Pagination methods
const previousPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    await fetchAgents();
  }
};

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    await fetchAgents();
  }
};

const goToCreateAgent = () => {
  // Logic to navigate to create agent page
  router.push({ name: "CreateAgentPage" });
};
</script>

<style scoped>
/* Add specific styles if necessary */
</style>
